import React, { useState, useEffect, useRef } from "react"
import {
  getPhysiotherapistDetails,
  saveAdditionalNotes,
  savePhysiotherapyAssessment
} from "../../../../core/services/api/patientClinicalService"
import SummaryView from "../../../../components/SummaryView"
import ConsultantNotesView from "../../../../components/ConsultantNotesView"
import JSONRenderer from "../../../../components/JSONRenderer/JSONRenderer"
import Toast from "../../../../components/Toast"
import "./Physiotherapist.scss"
import { MocUserRole } from "../../../../core/services/models/Enums"
import Button from "components/Button/Button"
import { getTemplatesByType, getVitals } from "core/services/api/patientDiagnosisService"
import moment from "moment"
import Print from "components/Print/Print"

const Physiotherapist: React.FunctionComponent<any> = (
  {
    patientInfo,
    recordDetails,
    appointmentDetails,
    isReadOnly,
    selectedIpdTab,
    staffType,
    smartSummaryDetails,
    fetchSmartSummary
  }
) => {

  const centerId = appointmentDetails?.centerId
  const consultantId = appointmentDetails?.consultantId
  const appointmentId = appointmentDetails?.id
  const appointmentType = appointmentDetails?.appointmentType
  const recordId = recordDetails?.id
  const patientId = appointmentDetails?.patientId
  const componentRef = useRef()

  const tabSection: string[] = [
    "Smart Summary",
    "Assessment",
    "Progress",
    "Follow up visit",
  ]

  const [vitalsData, setVitalsData] = useState<any>()
  const [selectedTab, setSelectedTab] = useState("Smart Summary")
  const [physioData, setPhysioData] = useState<any>()
  const [assessmentData, setAssessmentData] = useState({})
  const [physiotherapistAssessment, setPhysiotherapistAssessment] = useState<any>({})
  const [physiotherapistAssessmentPrint, setPhysiotherapistAssessmentPrint] = useState<any>({})
  const [progressNotes, setProgressNotes] = useState<any>("")
  const [isCompleted, setIsCompleted] = useState<any>(false)
  const [resConsultantNoteData, setResConsultantNoteData] = useState<any>({})
  const [isConsultantNoteData, setIsConsultantNoteData] = useState(false)
  const [progressNotesSaved, setProgressNotesSaved] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [smartSummaryData, setSmartSummaryData] = useState<any>()

  useEffect(() => {
    //Vitals
    if (recordId) {
      getVitals(recordId)
        .then((res: any) => {
          setVitalsData(res?.data)
        })
        .catch((err: any) => console.log(err?.message))
    }
  }, [])

  useEffect(() => {
    //Smart Summary Data
    if (smartSummaryDetails) {
      setSmartSummaryData(smartSummaryDetails)
    }
  }, [smartSummaryDetails])

  useEffect(() => {
    let tempJson: any, tempJsonPrint: any = {}
    getTemplatesByType("PHYSIOTHERAPIST")
      .then((res: any) => {
        //Assessment JSON
        tempJson = res?.find((e: any) => e?.subType === "Assessment")?.data[0]?.json || {}
        //Print JSON
        tempJsonPrint = res?.find((e: any) => e?.subType === "Assessment Print")?.data[0]?.json || {}
        tempJsonPrint["pages"][0]["readOnly"] = true
        tempJsonPrint["showNavigationButtons"] = "none"
        setPhysiotherapistAssessmentPrint(tempJsonPrint)
        //Read Only Check
        if (isReadOnly) {
          //Assessment
          tempJson["pages"][0]["readOnly"] = true
          tempJson["pages"][1]["readOnly"] = true
          tempJson["pages"][2]["readOnly"] = true
          setPhysiotherapistAssessment(tempJson)
        } else {
          setPhysiotherapistAssessment(tempJson)
        }
      })
      .catch((err: any) => console.log(err?.message))
  }, [isReadOnly])

  useEffect(() => {
    if (recordId && selectedTab !== "Follow up visit") {
      // setIsLoading(true)
      getPhysiotherapistDetails(recordId)
        .then((data: any) => {
          if (selectedTab === "Assessment") {
            const temp = { ...assessmentData, ...data?.assessment }
            if (!temp["diagnosis"]) {
              let diagnosisStr = ""
              smartSummaryData?.diagnosis?.forEach((each: any) => {
                diagnosisStr += `${each?.diagnosis ? each?.diagnosis : "NA"} (${each?.cancerType ? each?.cancerType : ""}). `
              })
              temp["diagnosis"] = diagnosisStr
            }
            if (!temp["todaysDate"]) {
              temp["todaysDate"] = moment().format("YYYY-MM-DD")
            }
            setAssessmentData(temp)
          }
          const progressData = data?.progressNotes
          setPhysioData(progressData)
          setProgressNotes(progressData?.notes)
          setIsLoading(false)
        })
        .catch((err: any) => {
          console.log(err)
          if (selectedTab === "Assessment") {
            const temp: any = {}
            if (!temp["diagnosis"]) {
              let diagnosisStr = ""
              smartSummaryData?.diagnosis?.forEach((each: any) => {
                diagnosisStr += `${each?.diagnosis ? each?.diagnosis : "NA"} (${each?.cancerType ? each?.cancerType : ""}). `
              })
              temp["diagnosis"] = diagnosisStr
            }
            if (isReadOnly) {
              temp["todaysDate"] = moment(appointmentDetails?.scheduleDate).format("YYYY-MM-DD")
            } else {
              temp["todaysDate"] = moment().format("YYYY-MM-DD")
            }
            setAssessmentData(temp)
          }
          setIsLoading(false)
        })
    }
  }, [selectedTab, appointmentDetails, recordDetails])

  useEffect(() => {
    setTimeout(() => {
      const completeBtn = document.getElementById("sv-nav-complete")
      if (isReadOnly && completeBtn) {
        completeBtn.style.display = "none"
      }
    }, 2000)
  }, [selectedTab])

  const handlePhysiotherapistAssessmentCompletion = (data: any) => {
    // setIsLoading(true)
    savePhysiotherapyAssessment(
      data,
      {
        centerId,
        consultantId,
        patientId,
        appointmentId,
        recordId,
      }
    )
      .then((res: any) => {
        setIsLoading(false)
        setIsCompleted(true)
        setAssessmentData(res?.assessment)
      })
      .catch((err: any) => {
        console.log(err)
        setIsLoading(false)
      })
  }

  const saveProgressNotes = () => {
    // setIsLoading(true)
    const temp = { ...physioData }
    temp["notes"] = progressNotes
    //API CALL
    saveAdditionalNotes(temp, appointmentId, recordId, "physiotherapist")
      .then((res: any) => {
        setIsLoading(false)
        setProgressNotesSaved(true)
      })
      .catch((err: any) => {
        console.log(err)
        setIsLoading(false)
      })
  }

  return (
    <div className="physio-page-container">
      {
        isLoading && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              right: "50%",
            }}
          >
            <div
              style={{ width: "3rem", height: "3rem" }}
              className="spinner-border text-primary"
            ></div>
          </div>
        )
      }
      <div className="patient-diagnosis">
        <div className="patient-smart-summary">
          <div className="tab-wrapper">
            {tabSection.map((tabname) => (
              <div
                className={`tab ${selectedTab === tabname ? "active" : ""}`}
                onClick={() => {
                  setSelectedTab(tabname)
                  if (tabname === "Smart Summary") {
                    fetchSmartSummary()
                  }
                }}
              >
                {tabname}
              </div>
            ))}
          </div>
          <div className="content-wrapper">
            {selectedTab === "Smart Summary" ? (
              <SummaryView
                patientDetail={patientInfo}
                smartSummaryDetails={smartSummaryDetails}
              />
            ) : selectedTab === "Follow up visit" ? (
              <ConsultantNotesView
                setResConsultantNoteData={setResConsultantNoteData}
                setIsConsultantNoteData={setIsConsultantNoteData}
                recordDetails={recordDetails}
                appointmentDetails={appointmentDetails}
                patientDetail={patientInfo}
                isReadOnly={isReadOnly}
                selectedIpdTab={selectedIpdTab}
                staffType={staffType}
                smartSummaryData={smartSummaryData}
              />
            ) : selectedTab === "Progress" ? (
              <div className="advice mt-4">
                <div className="heading">Notes :</div>
                <div className="textarea">
                  <textarea
                    name=""
                    id=""
                    cols={50}
                    rows={5}
                    value={progressNotes}
                    onChange={(e) => {
                      //write on change logic
                      console.log(e)
                      setProgressNotes(e?.target.value)
                    }}
                    disabled={isReadOnly}
                    className={isReadOnly ? "disabled" : ""}
                  />
                </div>
                <div className="save-button mt-4">
                  {isReadOnly ? "" :
                    <Button text="Save" id="save" onClick={saveProgressNotes}></Button>
                  }
                </div>
              </div>
            ) :
              <div>
                <span
                  className="print-button cursor-pointer"
                  style={{ display: "flex", justifyContent: "flex-end" }}
                >
                  <Print
                    componentRef={componentRef}
                  />
                </span>
                <JSONRenderer
                  json={physiotherapistAssessment}
                  printJson={physiotherapistAssessmentPrint}
                  appointmentDate={appointmentDetails?.scheduleDate}
                  data={assessmentData}
                  printData={assessmentData}
                  printRef={componentRef}
                  patientData={{ patientInfo: patientInfo, vitalsData: vitalsData }}
                  showPatientDetails={true}
                  handleCompletion={handlePhysiotherapistAssessmentCompletion}
                  origin={MocUserRole.PHYSIOTHERAPIST}
                  smartSummaryData={smartSummaryData}
                />
              </div>
            }
          </div>
        </div>
        {
          isCompleted ? (
            <Toast
              textMessage="Assessment data saved successfully"
              onCloseText={"Close"}
              onClose={() => {
                setIsCompleted(false)
                const temp = { ...physiotherapistAssessment }
                setPhysiotherapistAssessment({})
                setTimeout(() => {
                  setPhysiotherapistAssessment(temp)
                }, 0)
              }}
              type="success"
            ></Toast>
          ) : resConsultantNoteData?.id && isConsultantNoteData ? (
            <Toast
              textMessage={` Follow Up Data added Successfully`}
              onCloseText={"Close"}
              onClose={() => setIsConsultantNoteData(false)}
              type="success"
            ></Toast>
          ) : progressNotesSaved ? (
            <Toast
              textMessage={` Progress notes saved Successfully`}
              onCloseText={"Close"}
              onClose={() => setProgressNotesSaved(false)}
              type="success"
            ></Toast>
          ) : (
            ""
          )
        }
      </div>
    </div>
  )
}

export default Physiotherapist
